import bridge from "@vkontakte/vk-bridge";
import { pilotNameSchema } from "@frontier/protocol/auth-contracts";
import { z } from "zod";

const resultSchema = z.object({ result: z.literal(true) });
const userInfoSchema = z.object({
  id: z.number().int().positive(),
  first_name: z.string(),
  last_name: z.string(),
});
const INITIALIZE_TIMEOUT = 15000;
const USER_INFO_TIMEOUT = 2000;
let initialization: Promise<void> | null = null;
let displayNameRequest: Promise<string | undefined> | null = null;

/** Init releases the VK launch screen; it does not prove the player's identity. */
export function initializeVkGamesSdk(): Promise<void> {
  if (initialization) return initialization;
  const request = new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (accepted: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (accepted) resolve();
      else
        reject(
          new Error(
            "ВКонтакте не подтвердил запуск игры. Повтори подключение.",
          ),
        );
    };
    const timer = setTimeout(() => finish(false), INITIALIZE_TIMEOUT);
    void Promise.resolve()
      .then(() => bridge.send("VKWebAppInit"))
      .then(
        (result) => finish(resultSchema.safeParse(result).success),
        () => finish(false),
      );
  });
  initialization = request;
  void request.catch(() => {
    if (initialization === request) initialization = null;
  });
  return request;
}

/** Cosmetic metadata is optional and never substitutes for the signed launch proof. */
export function readVkGamesDisplayName(): Promise<string | undefined> {
  if (displayNameRequest) return displayNameRequest;
  const request = new Promise<string | undefined>((resolve) => {
    let settled = false;
    const finish = (name?: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(name);
    };
    const timer = setTimeout(() => finish(), USER_INFO_TIMEOUT);
    void initializeVkGamesSdk()
      .then(() => {
        if (!settled) return bridge.send("VKWebAppGetUserInfo");
      })
      .then(
        (result) => {
          if (settled) return;
          const user = userInfoSchema.safeParse(result);
          if (!user.success) return finish();
          const normalized = `${user.data.first_name} ${user.data.last_name}`
            .replace(
              /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\p{Default_Ignorable_Code_Point}]/gu,
              "",
            )
            .replace(/\s+/gu, " ")
            .trim();
          let bounded = "";
          for (const character of normalized) {
            if (bounded.length + character.length > 24) break;
            bounded += character;
          }
          const name = pilotNameSchema.safeParse(bounded.trim());
          finish(name.success ? name.data : undefined);
        },
        () => finish(),
      );
  });
  displayNameRequest = request;
  void request.then((name) => {
    if (name === undefined && displayNameRequest === request)
      displayNameRequest = null;
  });
  return request;
}
